import React, { useState, useEffect } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import Styles from './customerStyle.module.scss';
import Project from '../../common/Utils/project';
import { useAuth } from 'oidc-react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Building2, User, Phone, Mail, Calendar, Database, Shield } from 'lucide-react';
import api from '../../components/services/apiService';
import { useToast } from '../../providers/ToastProvider/ToastContext';
import { getCustomerName } from '../../components/services/TenantSettingsService';
import ConfirmationModal from '../../components/Modal/ConfirmationModal';
import PreviewDetails, { PreviewSection } from '../../components/Modal/PreviewDetails';

const CreateCustomerTable = () => {
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Validated form values waiting for confirmation in the preview; nothing is created until then.
  const [previewValues, setPreviewValues] = useState<any>(null);
  const [options, setOptions] = useState({
    patchManagement: false,
    vulnerabilityManagement: false,
    enableAIRemediation: false,
  });

  const initialValues = {
    name: '',
    type: '',
    totalLicenses: '',
    email: '',
    firstName: '',
    lastName: '',
    contactNum: '',
    details: '',
    expiryDate: '',
    dataStorageRegion: 'Default',
    patchManagement: false,
    vulnerabilityManagement: false,
    enableAIRemediation: false,
  };

  const validationSchema = Yup.object({
    name: Yup.string().required('Required'),
    type: Yup.string().required('Required'),
    totalLicenses: Yup.string().required('Required'),
    email: Yup.string().email('Invalid email').required('Required'),
    expiryDate: Yup.string().required('Required'),
  });

  const navigate = useNavigate();
  const { addToast } = useToast();

  // ...existing logic...
  const handleSubmit = (values: typeof initialValues) => {
    setPreviewValues({
      ...values,
      ...options,
    });
  };

  const getSquareStyle = (isActive: boolean) => ({
    width: '20px',
    height: '20px',
    borderRadius: '4px',
    border: `2px solid ${isActive ? '#22c55e' : '#52525b'}`,
    backgroundColor: isActive ? '#22c55e' : 'transparent',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '10px',
    transition: 'all 0.2s ease',
  });

  const handleToggle = (key: keyof typeof options) => {
    setOptions((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const auth = useAuth();
  const isReseller = auth?.userData?.profile?.role == 'reseller';

  const [resellerName, setResellerName] = useState('');
  useEffect(() => {
    if (isReseller && auth?.userData?.profile?.tenant_id) {
      getCustomerName(auth?.userData?.profile?.tenant_id as string).then(setResellerName);
    }
  }, [isReseller, auth?.userData?.profile?.tenant_id]);

  const createCustomer = async (values: any) => {
    try {
      setIsSubmitting(true);
      const {
        name,
        type,
        totalLicenses,
        email,
        details,
        expiryDate,
        dataStorageRegion,
        patchManagement,
        vulnerabilityManagement,
        enableAIRemediation,
        contactNum,
        firstName,
        lastName,
      } = values;

      if (auth && auth.userData) {
        const payload = {
          CustomerName: name,
          FirstName: firstName,
          LastName: lastName,
          CustomerType: type,
          ResellerName: isReseller ? resellerName : type === 'tenant' ? '' : name,
          TotalLicenses: String(totalLicenses),
          Email: email,
          DataStorageRegion: dataStorageRegion,
          Deleted: false,
          IsActive: true,
          IsTestTenant: false,
          Details: details || '',
          WebsiteUrl: '',
          ContactNumber: contactNum || '',
        };

        console.log('Customer creation payload:', payload);

        const response = await api.post(`${Project.api}tenantservice/Tenant/tenant-creation`, JSON.stringify(payload));

        if (response && response.data) {
          const createdTenantId = response.data.data?.tenantId;

          // AI Remediation is a separate toggle on the tenant: only call it once the tenant
          // exists, and only when the user actually asked for it (defaults to off otherwise).
          if (enableAIRemediation && createdTenantId) {
            try {
              await api.put(`${Project.api}tenantservice/Tenant/${createdTenantId}/ai-remediation`, JSON.stringify({ enable: true }));
            } catch (aiRemediationError) {
              console.error('Error enabling AI Remediation:', aiRemediationError);
              addToast('Customer created, but enabling AI Remediation failed. You can retry it from the customer settings.', 'error', 5000);
            }
          }

          addToast('Customer created successfully', 'success', 4000);
          setIsSuccess(true);
          setTimeout(() => {
            navigate('/customers');
          }, 3000);
        }
      }
    } catch (error) {
      console.error('Error updating data:', error);
      addToast('Failed to create customer', 'error', 4000);
    } finally {
      setIsSubmitting(false);
      setPreviewValues(null);
    }
  };

  // Only what createCustomer sends: Expiry Date and Patch Management don't reach the API yet (known issue #21).
  const getPreviewSections = (values: any): PreviewSection[] => [
    {
      title: 'Customer Information',
      rows: [
        { label: 'Customer Name', value: values.name },
        { label: 'Customer Type', value: values.type === 'reseller' ? 'Reseller' : 'Tenant' },
        ...(isReseller ? [{ label: 'Reseller', value: resellerName }] : []),
        { label: 'Total Licenses', value: values.totalLicenses },
        { label: 'Data Storage Region', value: values.dataStorageRegion },
      ],
    },
    {
      title: 'Primary Contact Details',
      rows: [
        { label: 'First Name', value: values.firstName },
        { label: 'Last Name', value: values.lastName },
        { label: 'Email Address', value: values.email },
        { label: 'Contact Number', value: values.contactNum },
      ],
    },
  ];

  const inputClass =
    'w-full border border-zinc-700 bg-[#161617] text-white p-2.5 rounded-lg text-sm focus:outline-none focus:border-zinc-500 focus:ring-1 focus:ring-zinc-500 transition-all placeholder-zinc-500';
  const labelClass = 'block text-sm font-medium text-zinc-300 mb-1.5';
  const sectionClass = 'bg-[#141414] border border-zinc-900 rounded-xl p-6 space-y-5';

  return (
    <div className="text-white p-6 space-y-6">
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {({ resetForm }) => (
          <Form className="space-y-6">
            {/* Section 1: Customer Info */}
            <div className={sectionClass}>
              <div className="flex items-center gap-2 mb-2 border-b border-zinc-800 pb-3">
                <Building2 className="w-5 h-5 text-zinc-400" />
                <h2 className="text-base font-semibold text-zinc-200">Customer Information</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelClass}>
                    Customer Name <span className="text-red-400">*</span>
                  </label>
                  <Field name="name" placeholder="Enter customer name" className={inputClass} />
                  <ErrorMessage name="name" component="div" className="text-red-400 text-xs mt-1" />
                </div>

                <div>
                  <label className={labelClass}>
                    Customer Type <span className="text-red-400">*</span>
                  </label>
                  <Field as="select" name="type" className={inputClass}>
                    <option value="">Select Type</option>
                    <option value="tenant">Tenant</option>
                    {!isReseller && <option value="reseller">Reseller</option>}
                  </Field>
                  <ErrorMessage name="type" component="div" className="text-red-400 text-xs mt-1" />
                </div>

                <div>
                  <label className={labelClass}>
                    Total Licenses <span className="text-red-400">*</span>
                  </label>
                  <Field type="number" name="totalLicenses" min="0" placeholder="Enter total licenses" className={inputClass} />
                  <ErrorMessage name="totalLicenses" component="div" className="text-red-400 text-xs mt-1" />
                </div>

                <div>
                  <label className={labelClass}>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      Expiry Date <span className="text-red-400">*</span>
                    </span>
                  </label>
                  <Field name="expiryDate" type="date" className={`${inputClass} custom-date`} />
                  <ErrorMessage name="expiryDate" component="div" className="text-red-400 text-xs mt-1" />
                </div>

                <div>
                  <label className={labelClass}>
                    <span className="flex items-center gap-1">
                      <Database className="w-3.5 h-3.5" />
                      Data Storage Region
                    </span>
                  </label>
                  <Field name="dataStorageRegion" type="text" readOnly className={`${inputClass} text-zinc-500 cursor-not-allowed`} />
                </div>
              </div>
            </div>

            {/* Section 2: Primary Contact */}
            <div className={sectionClass}>
              <div className="flex items-center gap-2 mb-2 border-b border-zinc-800 pb-3">
                <User className="w-5 h-5 text-zinc-400" />
                <h2 className="text-base font-semibold text-zinc-200">Primary Contact Details</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelClass}>
                    First Name <span className="text-red-400">*</span>
                  </label>
                  <Field name="firstName" placeholder="Enter first name" className={inputClass} />
                  <ErrorMessage name="firstName" component="div" className="text-red-400 text-xs mt-1" />
                </div>

                <div>
                  <label className={labelClass}>
                    Last Name <span className="text-red-400">*</span>
                  </label>
                  <Field name="lastName" placeholder="Enter last name" className={inputClass} />
                  <ErrorMessage name="lastName" component="div" className="text-red-400 text-xs mt-1" />
                </div>

                <div>
                  <label className={labelClass}>
                    <span className="flex items-center gap-1">
                      <Mail className="w-3.5 h-3.5" />
                      Email Address <span className="text-red-400">*</span>
                    </span>
                  </label>
                  <Field name="email" type="email" placeholder="Enter email address" className={inputClass} />
                  <ErrorMessage name="email" component="div" className="text-red-400 text-xs mt-1" />
                </div>

                <div>
                  <label className={labelClass}>
                    <span className="flex items-center gap-1">
                      <Phone className="w-3.5 h-3.5" />
                      Contact Number <span className="text-red-400">*</span>
                    </span>
                  </label>
                  <Field name="contactNum" placeholder="Enter contact number" className={inputClass} />
                  <ErrorMessage name="contactNum" component="div" className="text-red-400 text-xs mt-1" />
                </div>
              </div>
            </div>

            {/* Section 3: Features */}
            <div className={sectionClass}>
              {/* <div className="flex items-center gap-2 mb-2 border-b border-zinc-800 pb-3">
                <Shield className="w-5 h-5 text-zinc-400" />
                <h2 className="text-base font-semibold text-zinc-200">Features</h2>
              </div> */}

              <div className="flex flex-col space-y-3">
                <label
                  className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-zinc-800 hover:border-zinc-600 transition-all"
                  onClick={() => handleToggle('patchManagement')}
                >
                  <div style={getSquareStyle(options.patchManagement)}>
                    {options.patchManagement && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-zinc-200">Patch Management</p>
                    {/* <p className="text-xs text-zinc-500">Enable automated patch management for this customer</p> */}
                  </div>
                </label>

                {/* AI Remediation toggle hidden until the backend ai-remediation endpoint is available in production.
                    enableAIRemediation stays false, so the PUT in createCustomer is never sent.
                <label
                  className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-zinc-800 hover:border-zinc-600 transition-all"
                  onClick={() => handleToggle('enableAIRemediation')}
                >
                  <div style={getSquareStyle(options.enableAIRemediation)}>
                    {options.enableAIRemediation && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-zinc-200">AI Remediation</p>
                    <p className="text-xs text-zinc-500">Enable AI-driven remediation for this customer</p>
                  </div>
                </label>
                */}
              </div>
            </div>

            {/* Success Banner */}
            {isSuccess && (
              <div className="flex items-center gap-3 p-4 bg-green-900/30 border border-green-700 text-green-400 rounded-xl">
                <CheckCircle className="w-5 h-5 shrink-0" />
                <span className="text-sm font-medium">Customer created successfully! Redirecting...</span>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => resetForm()}
                className="px-5 py-2.5 text-sm font-medium text-zinc-300 border border-zinc-700 rounded-lg hover:bg-zinc-800 transition-all"
              >
                Clear
              </button>
              <button
                type="submit"
                disabled={isSubmitting || isSuccess}
                className="px-6 py-2.5 text-sm font-semibold bg-red-600 hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg transition-all flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    Creating...
                  </>
                ) : (
                  'Preview'
                )}
              </button>
            </div>
          </Form>
        )}
      </Formik>

      <ConfirmationModal
        show={!!previewValues}
        title="Review new customer"
        description="Check these details. The customer is created only after you confirm."
        confirmLabel="Create Customer"
        cancelLabel="Back to edit"
        loading={isSubmitting}
        onConfirm={() => createCustomer(previewValues)}
        onClose={() => setPreviewValues(null)}
      >
        {previewValues && <PreviewDetails sections={getPreviewSections(previewValues)} />}
      </ConfirmationModal>
    </div>
  );
};

export default CreateCustomerTable;
